<template>
  <header class="bg-background/30">
    <DesktopHeader
      class="hidden md:!flex"
      :searchValue="$route.query.search?.toString()"
      :search-autocomplete-values
      :cart-items-no
      :enable-search="!!$features?.search"
      @clear-all-searches="clearAllSearches"
      @get-matching-keywords="getMatchingKeywords"
      @remove-from-recent-searches="removeFromRecentSearches"
      @search-submit="onSearchSubmit"
    />

    <MobileHeader
      class="flex md:!hidden"
      :searchValue="$route.query.search?.toString()"
      :search-autocomplete-values
      :cart-items-no
      :enable-search="!!$features?.search"
      @clear-all-searches="clearAllSearches"
      @get-matching-keywords="getMatchingKeywords"
      @remove-from-recent-searches="removeFromRecentSearches"
      @search-submit="onSearchSubmit"
      @openDrawer="$emit('openDrawer')"
    />
  </header>
</template>

<script lang="ts" setup>
import type { AutocompleteType } from "~/types/schemas/common/Molecule/searchbar";
import DesktopHeader from "~/components/informative/Organism/Header/Desktop.vue";
import MobileHeader from "~/components/informative/Organism/Header/Mobile.vue";
import useSearchProducts from "~/composables/informative/useSearchProducts";
import paths from "~/constants/paths";
import useCart from "~/composables/checkout/useCart";
import { useDebounceFn } from "@vueuse/core";

const {
  suggestions,
  recentSearches,
  getSearchData,
  addToRecentSearches,
  removeFromRecentSearches,
  clearAllSearches,
} = useSearchProducts();
const { cart } = useCart();
const router = useRouter();
const route = useRoute();

const cartItemsNo = computed(() => {
  const quantity = cart.value?.total_quantity || 0;
  return quantity > 99 ? "99+" : quantity;
});

const searchAutocompleteValues = computed<AutocompleteType>(() => {
  if (!route.query.search) {
    return {
      key: "recent",
      title: "Recent Searches",
      items: recentSearches.value,
    };
  } else {
    return {
      key: "matching",
      title: "Matching Keywords",
      items: suggestions.value,
    };
  }
});

const getMatchingKeywords = useDebounceFn(async (value: string) => {
  await getSearchData(value);
}, 500);

const onSearchSubmit = (value: string) => {
  if (!value && !route.query.search) return;
  if (value) {
    addToRecentSearches(value);
  }
  router.push({
    path: paths.products,
    query: {
      search: value || undefined,
    },
  });
};
</script>

<style>
.search {
  .field-input {
    @apply !py-3 !text-size_4 leading-[100%];
  }

  .prefix-icon {
    @apply text-neutral-600;
  }

  &:focus-within {
    .prefix-icon {
      @apply text-neutral-900;
    }
  }
}
</style>
